/**
 * GeckoTerminal Token Pricing Service
 *
 * Features:
 * - Rate limiting with exponential backoff (30 calls/minute)
 * - In-memory caching with TTL
 * - Network ID mapping and discovery
 * - Batch request handling (up to 30 addresses per call)
 * - Error handling and retry logic
 */

import { GeckoApi } from '@/lib/gecko-api'
import { getPricingDB, type TokenPool, type CachedTokenPrice } from './pricing-db'

// ============================================================================
// Utility Functions
// ============================================================================

/**
 * Normalize address by removing chain prefix and converting to lowercase
 * Example: "eth_0xABC..." -> "0xabc..."
 * Example: "solana_EPjF..." -> "epjf..."
 */
function normalizeAddress(address: string): string {
    if (!address) return ''

    // Remove chain prefix (anything before underscore)
    const withoutPrefix = address.includes('_')
        ? address.split('_')[1]
        : address

    return withoutPrefix.toLowerCase()
}

// ============================================================================
// Types
// ============================================================================

export interface TokenPrice {
    address: string
    symbol: string
    name: string
    decimals: number

    // Visual
    image_url: string | null

    // Pricing
    price_usd: string
    fdv_usd: string | null
    market_cap_usd: string | null
    total_reserve_in_usd: string | null

    // Volume
    volume_usd_24h: string | null

    // Supply
    total_supply: string | null
    normalized_total_supply: string | null
    circulating_supply: string | null

    // CoinGecko integration
    coingecko_coin_id: string | null

    // Legacy fields for backwards compatibility
    price_change_24h: number
    volume_24h: string
    market_cap: string
    fdv: string | null
    networks: string[]

    // Associated pools (sorted by liquidity)
    pools: TokenPool[]

    // Metadata
    timestamp: number
}

// Re-export types from pricing-db for convenience
export type { TokenPool, CachedTokenPrice } from './pricing-db'

export interface PricingCacheEntry {
    data: TokenPrice
    expiresAt: number
}

export interface RateLimitConfig {
    maxCallsPerMinute: number
    initialBackoffMs: number
    maxBackoffMs: number
    backoffMultiplier: number
}

export interface NetworkMapping {
    chainId: number | string
    networkId: string
    chainName: string
}

// ============================================================================
// Rate Limiter
// ============================================================================

class RateLimiter {
    private callTimestamps: number[] = []
    private backoffMs: number
    private isBackingOff = false

    constructor(private config: RateLimitConfig) {
        this.backoffMs = config.initialBackoffMs
    }

    /**
     * Check if we can make a call, wait if needed
     */
    async waitForSlot(): Promise<void> {
        const now = Date.now()
        const oneMinuteAgo = now - 60000

        // Remove timestamps older than 1 minute
        this.callTimestamps = this.callTimestamps.filter((ts) => ts > oneMinuteAgo)

        // If we've hit the rate limit, apply backoff
        if (this.callTimestamps.length >= this.config.maxCallsPerMinute) {
            this.isBackingOff = true
            console.warn(`[RateLimiter] Rate limit reached, backing off for ${this.backoffMs}ms`)

            await new Promise((resolve) => setTimeout(resolve, this.backoffMs))

            // Increase backoff for next time
            this.backoffMs = Math.min(
                this.backoffMs * this.config.backoffMultiplier,
                this.config.maxBackoffMs
            )

            // Recursively check again
            return this.waitForSlot()
        }

        // Record this call
        this.callTimestamps.push(now)

        // Reset backoff if we successfully made a call without hitting limit
        if (this.isBackingOff) {
            console.log('[RateLimiter] Backoff successful, resetting')
            this.backoffMs = this.config.initialBackoffMs
            this.isBackingOff = false
        }
    }

    /**
     * Get current rate limit status
     */
    getStatus() {
        const now = Date.now()
        const oneMinuteAgo = now - 60000
        const recentCalls = this.callTimestamps.filter((ts) => ts > oneMinuteAgo).length

        return {
            callsInLastMinute: recentCalls,
            remainingCalls: this.config.maxCallsPerMinute - recentCalls,
            isBackingOff: this.isBackingOff,
            currentBackoffMs: this.backoffMs,
        }
    }
}

// ============================================================================
// Pricing Cache
// ============================================================================

class PricingCache {
    private cache = new Map<string, PricingCacheEntry>()

    constructor(private ttlMs: number = 60000) { } // Default 1 minute TTL

    /**
     * Get cached price for a token
     */
    get(address: string, chainId: number | string): TokenPrice | null {
        const key = this.getKey(address, chainId)
        const entry = this.cache.get(key)

        if (!entry) return null

        // Check if expired
        if (Date.now() > entry.expiresAt) {
            this.cache.delete(key)
            return null
        }

        return entry.data
    }

    /**
     * Set cached price for a token
     */
    set(address: string, chainId: number | string, data: TokenPrice): void {
        const key = this.getKey(address, chainId)
        this.cache.set(key, {
            data,
            expiresAt: Date.now() + this.ttlMs,
        })
    }

    /**
     * Get multiple cached prices
     */
    getMany(addresses: string[], chainId: number | string): Map<string, TokenPrice> {
        const result = new Map<string, TokenPrice>()

        for (const address of addresses) {
            const price = this.get(address, chainId)
            if (price) {
                result.set(address.toLowerCase(), price)
            }
        }

        return result
    }

    /**
     * Set multiple cached prices
     */
    setMany(prices: TokenPrice[], chainId: number | string): void {
        for (const price of prices) {
            this.set(price.address, chainId, price)
        }
    }

    /**
     * Clear cache
     */
    clear(): void {
        this.cache.clear()
    }

    /**
     * Clear expired entries
     */
    clearExpired(): void {
        const now = Date.now()
        for (const [key, entry] of this.cache.entries()) {
            if (now > entry.expiresAt) {
                this.cache.delete(key)
            }
        }
    }

    /**
     * Get cache stats
     */
    getStats() {
        const now = Date.now()
        let expired = 0
        let active = 0

        for (const entry of this.cache.values()) {
            if (now > entry.expiresAt) {
                expired++
            } else {
                active++
            }
        }

        return {
            totalEntries: this.cache.size,
            activeEntries: active,
            expiredEntries: expired,
        }
    }

    private getKey(address: string, chainId: number | string): string {
        return `${chainId}:${address.toLowerCase()}`
    }
}

// ============================================================================
// Network ID Mapper
// ============================================================================

class NetworkMapper {
    // Known network mappings (chainId -> GeckoTerminal networkId)
    private static KNOWN_MAPPINGS: Record<number | string, string> = {
        // EVM Chains
        1: 'eth', // Ethereum
        2000: 'dogechain', // Dogechain
        109: 'shibarium',
        56: 'bsc', // Binance Smart Chain
        137: 'polygon_pos', // Polygon
        42161: 'arbitrum', // Arbitrum One
        10: 'optimism', // Optimism
        43114: 'avalanche', // Avalanche C-Chain
        250: 'fantom', // Fantom
        8453: 'base', // Base
        59144: 'linea', // Linea
        324: 'zksync', // zkSync Era
        534352: 'scroll', // Scroll
        5000: 'mantle', // Mantle
        81457: 'blast', // Blast

        // Solana
        'solana-mainnet': 'solana',
        'solana-testnet': 'solana',
        'solana-devnet': 'solana',
    }

    // Cache for discovered network IDs
    private discoveredMappings = new Map<number | string, string>()
    private api: GeckoApi<unknown>

    constructor() {
        this.api = new GeckoApi<unknown>()
    }

    /**
     * Get GeckoTerminal network ID for a chain
     * @param chainId - Chain ID (number or string)
     * @param chainName - Optional chain name to use as fallback
     */
    async getNetworkId(chainId: number | string, chainName?: string): Promise<string | null> {
        // Check known mappings first
        const known = NetworkMapper.KNOWN_MAPPINGS[chainId]
        if (known) return known

        // Check discovered mappings
        const discovered = this.discoveredMappings.get(chainId)
        if (discovered) return discovered

        // Try chain name fallback (convert to lowercase with underscores)
        if (chainName) {
            const nameFallback = chainName
                .toLowerCase()
                .replace(/\s+/g, '_')  // Replace spaces with underscores
                .replace(/[^a-z0-9_]/g, '')  // Remove special characters

            // Cache and return the name-based fallback
            // GeckoTerminal typically uses lowercase chain names with underscores
            console.log(`[NetworkMapper] Using chain name fallback: ${nameFallback} for chain ${chainId}`)
            this.discoveredMappings.set(chainId, nameFallback)
            return nameFallback
        }

        // Try to discover from API
        try {
            const networkId = await this.discoverNetworkId(chainId)
            if (networkId) {
                this.discoveredMappings.set(chainId, networkId)
                return networkId
            }
        } catch (error) {
            console.error(`[NetworkMapper] Failed to discover network ID for chain ${chainId}:`, error)
        }

        return null
    }

    /**
     * Discover network ID from GeckoTerminal API
     */
    private async discoverNetworkId(chainId: number | string): Promise<string | null> {
        try {
            // Fetch all networks from GeckoTerminal
            const response = await this.api.networks.networksList({ page: 1 })

            if (!response || !response.data || !Array.isArray(response.data)) return null

            // Try to find matching network by chain ID or name
            for (const network of response.data) {
                // Match by attributes if available
                const networkData = network as any

                // Some networks might have chain_identifier or similar fields
                if (
                    networkData.attributes?.chain_identifier === chainId ||
                    networkData.attributes?.chain_id === chainId
                ) {
                    console.log("Discovered network identifier", networkData.id)
                    return networkData.id
                }
            }

            return null
        } catch (error) {
            console.error('[NetworkMapper] Discovery error:', error)
            return null
        }
    }

    /**
     * Add custom network mapping
     */
    addMapping(chainId: number | string, networkId: string): void {
        this.discoveredMappings.set(chainId, networkId)
    }

    /**
     * Get all known mappings
     */
    getAllMappings(): Record<number | string, string> {
        return {
            ...NetworkMapper.KNOWN_MAPPINGS,
            ...Object.fromEntries(this.discoveredMappings),
        }
    }
}

// ============================================================================
// Pricing Service
// ============================================================================

export class PricingService {
    private api: GeckoApi<unknown>
    private rateLimiter: RateLimiter
    private cache: PricingCache
    private networkMapper: NetworkMapper
    private db: ReturnType<typeof getPricingDB>
    private cacheTtlMs: number

    constructor(
        rateLimitConfig: Partial<RateLimitConfig> = {},
        cacheTtlMs: number = 60000
    ) {
        this.api = new GeckoApi<unknown>()

        this.rateLimiter = new RateLimiter({
            maxCallsPerMinute: 30,
            initialBackoffMs: 2000,
            maxBackoffMs: 30000,
            backoffMultiplier: 2,
            ...rateLimitConfig,
        })

        this.cache = new PricingCache(cacheTtlMs)
        this.networkMapper = new NetworkMapper()
        this.db = getPricingDB()
        this.cacheTtlMs = cacheTtlMs
    }

    /**
     * Get prices for multiple tokens on a specific chain
     * Automatically handles batching (30 addresses per request)
     */
    async getTokenPrices(
        addresses: string[],
        chainId: number | string,
        options: { forceRefresh?: boolean; includeNetworks?: boolean; chainName?: string } = {}
    ): Promise<Map<string, TokenPrice>> {
        const normalizedAddresses = addresses.map((addr) => addr.toLowerCase())
        const result = new Map<string, TokenPrice>()

        // Check IndexedDB cache first (unless force refresh)
        if (!options.forceRefresh) {
            const cachedFromDB = await this.db.getCachedTokenPrices(chainId, normalizedAddresses)

            // Convert CachedTokenPrice to TokenPrice format
            cachedFromDB.forEach((cached, address) => {
                const price = this.convertCachedToTokenPrice(cached)
                result.set(address, price)
            })

            const uncachedAddresses = normalizedAddresses.filter((addr) => !cachedFromDB.has(addr))

            if (uncachedAddresses.length === 0) {
                return result
            }
        }

        const addressesToFetch = options.forceRefresh ? normalizedAddresses :
            normalizedAddresses.filter((addr) => !result.has(addr))

        // Get network ID from mapper (converts chainId to GeckoTerminal network slug)
        const networkId = await this.networkMapper.getNetworkId(chainId, options.chainName)
        if (!networkId) {
            console.error(`[PricingService] No network ID found for chainId ${chainId}${options.chainName ? ` (${options.chainName})` : ''}`)
            return result
        }

        // Batch addresses into groups of 30
        const batches = this.batchAddresses(addressesToFetch, 30)

        // Fetch each batch with rate limiting
        for (let i = 0; i < batches.length; i++) {
            const batch = batches[i]

            try {
                // Wait for rate limit slot
                await this.rateLimiter.waitForSlot()

                // Make API call with pool data included
                const response = await this.api.networks.tokensMultiDetail(
                    networkId.toString(),
                    batch.join(','),
                    {
                        include: 'top_pools'
                    }
                )

                // The API wrapper might wrap the response - check for nested data
                const actualData = (response as any)?.data?.data || response?.data
                const isDataArray = Array.isArray(actualData)

                if (response && actualData && isDataArray) {
                    // Parse included pools (access via any cast since TypeScript doesn't know about included)
                    const responseAny = response as any

                    // Check for included at both levels (response.included or response.data.included)
                    const includedPools = responseAny.data?.included || responseAny.included || []

                    const poolsMap = this.parseIncludedPools(includedPools)

                    // Process response with pools
                    const prices = await this.parseTokenPricesWithPools(
                        actualData,
                        poolsMap,
                        chainId
                    )

                    // Add to result
                    prices.forEach((price: TokenPrice) => {
                        result.set(price.address.toLowerCase(), price)
                    })
                }
            } catch (error) {
                console.error(`[PricingService] Failed to fetch batch ${i + 1}:`, error)
                // Continue with next batch instead of failing completely
            }
        }

        return result
    }

    /**
     * Get price for a single token
     */
    async getTokenPrice(
        address: string,
        chainId: number | string,
        forceRefresh = false
    ): Promise<TokenPrice | null> {
        const prices = await this.getTokenPrices([address], chainId, { forceRefresh })
        return prices.get(address.toLowerCase()) || null
    }

    /**
     * Clear cache
     */
    clearCache(): void {
        this.cache.clear()
    }

    /**
     * Clear expired cache entries
     */
    clearExpiredCache(): void {
        this.cache.clearExpired()
    }

    /**
     * Add custom network mapping
     */
    addNetworkMapping(chainId: number | string, networkId: string): void {
        this.networkMapper.addMapping(chainId, networkId)
    }

    /**
     * Get service statistics
     */
    getStats() {
        return {
            cache: this.cache.getStats(),
            rateLimit: this.rateLimiter.getStatus(),
            networks: this.networkMapper.getAllMappings(),
        }
    }

    /**
     * Parse included pools from API response
     */
    private parseIncludedPools(included: any[]): Map<string, TokenPool> {
        const poolsMap = new Map<string, TokenPool>()

        if (!included || !Array.isArray(included)) return poolsMap

        for (const item of included) {
            if (item.type !== 'pool') continue

            const pool: TokenPool = {
                id: item.id,
                address: normalizeAddress(item.attributes?.address || item.id),
                name: item.attributes?.name || '',
                poolCreatedAt: item.attributes?.pool_created_at || '',

                // Liquidity
                reserveInUsd: item.attributes?.reserve_in_usd || '0',

                // Base token pricing
                baseTokenPriceUsd: item.attributes?.base_token_price_usd || '0',
                baseTokenPriceNative: item.attributes?.base_token_price_native_currency || '0',
                baseTokenBalance: item.attributes?.base_token_balance || '0',

                // Quote token pricing
                quoteTokenPriceUsd: item.attributes?.quote_token_price_usd || '0',
                quoteTokenPriceNative: item.attributes?.quote_token_price_native_currency || '0',
                quoteTokenBalance: item.attributes?.quote_token_balance || '0',

                // Price relationships
                baseTokenPriceQuoteToken: item.attributes?.base_token_price_quote_token || '0',
                quoteTokenPriceBaseToken: item.attributes?.quote_token_price_base_token || '0',

                // Market stats
                fdvUsd: item.attributes?.fdv_usd || '0',
                marketCapUsd: item.attributes?.market_cap_usd || item.attributes?.fdv_usd || '0',

                // Price changes
                priceChangePercentage: item.attributes?.price_change_percentage || {
                    m5: '0',
                    m15: '0',
                    m30: '0',
                    h1: '0',
                    h6: '0',
                    h24: '0',
                },

                // Transactions
                transactions: item.attributes?.transactions || {
                    m5: { buys: 0, sells: 0, buyers: 0, sellers: 0 },
                    m15: { buys: 0, sells: 0, buyers: 0, sellers: 0 },
                    m30: { buys: 0, sells: 0, buyers: 0, sellers: 0 },
                    h1: { buys: 0, sells: 0, buyers: 0, sellers: 0 },
                    h6: { buys: 0, sells: 0, buyers: 0, sellers: 0 },
                    h24: { buys: 0, sells: 0, buyers: 0, sellers: 0 },
                },

                // Volume
                volumeUsd: item.attributes?.volume_usd || {
                    m5: '0',
                    m15: '0',
                    m30: '0',
                    h1: '0',
                    h6: '0',
                    h24: '0',
                },

                // Relationships (normalize addresses)
                baseTokenAddress: normalizeAddress(item.relationships?.base_token?.data?.id || ''),
                quoteTokenAddress: normalizeAddress(item.relationships?.quote_token?.data?.id || ''),
                dexId: item.relationships?.dex?.data?.id || '',
            }

            poolsMap.set(pool.id, pool)
        }

        return poolsMap
    }

    /**
     * Parse token prices with pools from API response and cache in IndexedDB
     */
    private async parseTokenPricesWithPools(
        data: any[],
        poolsMap: Map<string, TokenPool>,
        chainId: number | string
    ): Promise<TokenPrice[]> {
        const prices: TokenPrice[] = []

        for (const item of data) {
            const attrs = item.attributes || {}

            // Get pools for this token
            const poolIds = item.relationships?.top_pools?.data || []
            const tokenPools: TokenPool[] = poolIds
                .map((poolRef: any) => poolsMap.get(poolRef.id))
                .filter((pool: TokenPool | undefined): pool is TokenPool => pool !== undefined)
                .sort(
                    (a: TokenPool, b: TokenPool) =>
                        parseFloat(b.reserveInUsd || '0') -
                        parseFloat(a.reserveInUsd || '0')
                )

            const tokenPrice: TokenPrice = {
                address: normalizeAddress(attrs.address || item.id),
                symbol: attrs.symbol || '',
                name: attrs.name || '',
                decimals: attrs.decimals || 0,

                // Visual
                image_url: attrs.image_url || null,

                // Pricing
                price_usd: attrs.price_usd || '0',
                fdv_usd: attrs.fdv_usd || null,
                market_cap_usd: attrs.market_cap_usd || null,
                total_reserve_in_usd: attrs.total_reserve_in_usd || null,

                // Volume
                volume_usd_24h: attrs.volume_usd?.h24 || null,

                // Supply
                total_supply: attrs.total_supply || null,
                normalized_total_supply: attrs.normalized_total_supply || null,
                circulating_supply: attrs.circulating_supply || null,

                // CoinGecko integration
                coingecko_coin_id: attrs.coingecko_coin_id || null,

                // Legacy fields for backwards compatibility
                price_change_24h: parseFloat(
                    tokenPools[0]?.priceChangePercentage?.h24 || '0'
                ),
                volume_24h: attrs.volume_usd?.h24 || '0',
                market_cap: attrs.market_cap_usd || '0',
                fdv: attrs.fdv_usd || null,
                networks: item.relationships?.network?.data
                    ? [item.relationships.network.data.id]
                    : [],

                // Pools
                pools: tokenPools,

                // Metadata
                timestamp: Date.now(),
            }

            prices.push(tokenPrice)

            // Cache in IndexedDB
            try {
                await this.db.cacheTokenPrice(
                    chainId,
                    attrs,
                    tokenPools,
                    this.cacheTtlMs
                )
            } catch (error) {
                console.error('[PricingService] Failed to cache token in IndexedDB:', error)
            }
        }

        return prices
    }

    /**
     * Convert CachedTokenPrice to TokenPrice
     */
    private convertCachedToTokenPrice(cached: CachedTokenPrice): TokenPrice {
        return {
            address: cached.address,
            symbol: cached.symbol,
            name: cached.name,
            decimals: cached.decimals,

            // Visual
            image_url: cached.imageUrl,

            // Pricing
            price_usd: cached.priceUsd,
            fdv_usd: cached.fdvUsd,
            market_cap_usd: cached.marketCapUsd || cached.fdvUsd || '0',
            total_reserve_in_usd: cached.totalReserveInUsd,

            // Volume
            volume_usd_24h: cached.volumeUsd24h,

            // Supply
            total_supply: cached.totalSupply,
            normalized_total_supply: cached.normalizedTotalSupply,
            circulating_supply: cached.circulatingSupply,

            // CoinGecko integration
            coingecko_coin_id: cached.coingeckoCoinId,

            // Legacy fields
            price_change_24h: parseFloat(cached.pools[0]?.priceChangePercentage?.h24 || '0'),
            volume_24h: cached.volumeUsd24h || '0',
            market_cap: cached.marketCapUsd || cached.fdvUsd || '0',
            fdv: cached.fdvUsd,
            networks: [],

            // Pools
            pools: cached.pools,

            // Metadata
            timestamp: cached.timestamp,
        }
    }

    /**
     * Batch addresses into groups of specified size
     */
    private batchAddresses(addresses: string[], batchSize: number): string[][] {
        const batches: string[][] = []

        for (let i = 0; i < addresses.length; i += batchSize) {
            batches.push(addresses.slice(i, i + batchSize))
        }

        return batches
    }
}

// ============================================================================
// Singleton Instance
// ============================================================================

let globalPricingService: PricingService | null = null

/**
 * Get or create global pricing service instance
 */
export function getPricingService(
    config?: { rateLimitConfig?: Partial<RateLimitConfig>; cacheTtlMs?: number }
): PricingService {
    if (!globalPricingService) {
        globalPricingService = new PricingService(config?.rateLimitConfig, config?.cacheTtlMs)
    }
    return globalPricingService
}

/**
 * Reset global pricing service (useful for testing)
 */
export function resetPricingService(): void {
    globalPricingService = null
}
